import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from 'react-bootstrap';
import PageShell from '../Components/ui/PageShell';
import DashboardCard from '../Components/ui/DashboardCard';
import DashboardDateFilter from '../Components/ui/DashboardDateFilter';
import AppModal from '../Components/ui/AppModal';
import DataTable from '../Components/ui/DataTable';
import { getUserDetailsHome } from '../services/userService';
import { getAdminRequirements } from '../services/requirementService';
import { getClientsList } from '../services/clientService';
import { formatDisplayDate } from '../utils/dateFormat';
import {
  ADMIN_DASHBOARD_HR_FOLLOW_UP_CARD,
  computeAdminDashboardStats,
  computeAdminDashboardDrillDown,
  formatRequirementSummary,
  isActiveOpenRequirement,
} from '../utils/dashboardStats';
import { getDefaultDashboardDateRange } from '../utils/requirementDateFilter';
import {
  getPrioritySortIndex,
  normalizeRequirementPriority,
} from '../utils/requirementPriority';
import { normalizeRequirementType } from '../utils/requirementType';
import { useLayout } from '../layout/AppLayout';
import DashboardSourceProfileSearch, {
  DashboardSourceProfileSearchBar,
  DashboardSourceProfileSearchResults,
} from '../Components/dashboard/DashboardSourceProfileSearch';

const DEFAULT_DATE_RANGE = getDefaultDashboardDateRange();

const quickLinks = [
  { title: 'Add Requirement', description: 'Create a new job requirement', action: 'create-requirement' },
  { title: 'Requirement List', description: 'View and manage all requirements', to: '/Requirments' },
  { title: 'Create User', description: 'Add recruiters and team leads', action: 'create-user' },
  { title: 'User List', description: 'Manage system users', to: '/users/list' },
  { title: 'Add Client', description: 'Register a new client', action: 'create-client' },
  { title: 'Client List', description: 'Browse client accounts', to: '/clients/list' },
  { title: 'Interviews', description: 'Upcoming interview schedule', to: '/work/interviews' },
  { title: 'Status Tracking', description: 'Pipeline stages and candidate drill-down', to: '/reports/tracking' },
  { title: 'Analytics', description: 'Recruitment funnel and performance', to: '/reports/analytics' },
];

function AdminDashboard() {
  const { openFormModal } = useLayout();
  const [requirements, setRequirements] = useState([]);
  const [clients, setClients] = useState({});
  const [userList, setUserList] = useState([]);
  const [dateRange, setDateRange] = useState(DEFAULT_DATE_RANGE);
  const [loadError, setLoadError] = useState('');
  const [drillDownModal, setDrillDownModal] = useState({
    show: false,
    title: '',
    rows: [],
    showInterviewDate: false,
    mode: 'candidate',
  });

  useEffect(() => {
    const load = async () => {
      try {
        setLoadError('');
        const [requirementsData, clientsData, usersData] = await Promise.all([
          getAdminRequirements(),
          getClientsList(),
          getUserDetailsHome(),
        ]);

        setRequirements(Array.isArray(requirementsData) ? requirementsData : []);
        setClients(clientsData || {});
        setUserList(Array.isArray(usersData) ? usersData : []);
      } catch (error) {
        console.error('Dashboard load failed:', error);
        setLoadError(error.message || 'Failed to load dashboard data.');
      }
    };
    load();

    const handleRefresh = () => {
      load();
    };

    window.addEventListener('focus', handleRefresh);
    return () => window.removeEventListener('focus', handleRefresh);
  }, []);

  const stats = useMemo(
    () => computeAdminDashboardStats(requirements, clients, userList, dateRange),
    [requirements, clients, userList, dateRange]
  );

  const drillDown = useMemo(
    () => computeAdminDashboardDrillDown(requirements, dateRange, userList),
    [requirements, dateRange, userList]
  );

  const recentRequirements = useMemo(() => (
    requirements
      .filter((req) => normalizeRequirementType(req.requirementtype || req.RequirmentType) === 'High')
      .filter((req) => normalizeRequirementPriority(req.priority) !== null)
      .filter((req) => isActiveOpenRequirement(req.requirementtype || req.RequirmentType))
      .sort((a, b) => {
        const priorityDiff = getPrioritySortIndex(a.priority) - getPrioritySortIndex(b.priority);
        if (priorityDiff !== 0) return priorityDiff;
        return new Date(b.startDate || 0) - new Date(a.startDate || 0);
      })
      .slice(0, 5)
      .map(formatRequirementSummary)
  ), [requirements]);

  const openDrillDownModal = ({ title, rows, showInterviewDate = false, mode = 'candidate' }) => {
    setDrillDownModal({
      show: true,
      title,
      rows: Array.isArray(rows) ? rows : [],
      showInterviewDate,
      mode,
    });
  };

  const closeDrillDownModal = () => {
    setDrillDownModal({
      show: false,
      title: '',
      rows: [],
      showInterviewDate: false,
      mode: 'candidate',
    });
  };

  const requirementDrillDownColumns = useMemo(() => [
    { key: 'regId', label: 'Req ID', sortable: true },
    { key: 'client', label: 'Client', sortable: true },
    { key: 'role', label: 'Role', sortable: true },
    { key: 'requirementType', label: 'Type', sortable: true },
    {
      key: 'startDate',
      label: 'Start Date',
      sortable: true,
      render: (row) => formatDisplayDate(row.startDate),
    },
    {
      key: 'requirementId',
      label: 'Action',
      render: (row) => (
        <Link to={`/Requirments?requirementId=${encodeURIComponent(row.requirementId || '')}`}>
          Open
        </Link>
      ),
    },
  ], []);

  const candidateColumns = useMemo(() => {
    const columns = [
      { key: 'candidateName', label: 'Source Profile', sortable: true },
    ];

    if (drillDownModal.mode === 'hrFollowUp') {
      columns.push({ key: 'employeeName', label: 'Employee Name', sortable: true });
    }

    columns.push(
      { key: 'client', label: 'Client', sortable: true },
      { key: 'role', label: 'Role', sortable: true },
      { key: 'regId', label: 'Req ID', sortable: true },
      { key: 'status', label: 'Status', sortable: true, type: 'status', useStoredLabel: true },
    );

    if (drillDownModal.mode === 'hrFollowUp') {
      columns.push({ key: 'statusDateLabel', label: 'Status Date', sortable: true });
    }

    if (drillDownModal.showInterviewDate) {
      columns.push({ key: 'interviewDateLabel', label: 'Date & Time', sortable: true });
    }

    columns.push({
      key: 'requirementId',
      label: 'Action',
      render: (row) => (
        <Link to={`/Requirments?requirementId=${encodeURIComponent(row.requirementId || '')}`}>
          View Requirement
        </Link>
      ),
    });

    return columns;
  }, [drillDownModal.showInterviewDate, drillDownModal.mode]);

  const drillDownModalColumns = drillDownModal.mode === 'requirement'
    ? requirementDrillDownColumns
    : candidateColumns;

  const drillDownRowKey = drillDownModal.mode === 'requirement'
    ? 'requirementId'
    : 'candidateId';

  const drillDownSearchKeys = drillDownModal.mode === 'requirement'
    ? ['regId', 'client', 'role', 'requirementType']
    : drillDownModal.mode === 'hrFollowUp'
      ? ['candidateName', 'employeeName', 'client', 'role', 'regId', 'status', 'statusDateLabel']
      : drillDownModal.showInterviewDate
        ? ['candidateName', 'client', 'role', 'regId', 'status', 'interviewDateLabel']
        : ['candidateName', 'client', 'role', 'regId', 'status'];

  return (
    <DashboardSourceProfileSearch>
    <PageShell
      title="Admin Dashboard"
      subtitle="Overview of recruitment operations, users, and pipeline health."
      breadcrumbs={[{ label: 'Dashboard', path: '/dashboard' }]}
      actions={<DashboardSourceProfileSearchBar />}
    >
      {loadError && <div className="alert alert-danger">{loadError}</div>}

      <DashboardDateFilter
        onApply={setDateRange}
        onReset={setDateRange}
      />

      <p className="hr-dashboard-filter-hint">
        Showing:
        {dateRange.from ? ` ${dateRange.from}` : ''}
        {dateRange.to ? ` → ${dateRange.to}` : ''}
        {' · '}
        reqs by start date, profiles by upload date, interviews by scheduled date; HR Follow-up by latest status date; open requirements are all active reqs
      </p>

      <DashboardSourceProfileSearchResults />

      <div className="hr-stat-grid">
        <DashboardCard title="Status Tracking" value="View" icon="📊" accent="teal" to="/reports/tracking" subtitle="Pipeline by stage" />
        <DashboardCard title="Analytics" value="View" icon="📈" accent="green" to="/reports/analytics" subtitle="Funnel & performance" />
        <DashboardCard title="Profiles View" value="View" icon="👁️" accent="teal" to="/admin/profiles-view" subtitle="Source profiles by date" />
        <DashboardCard
          title="Open Requirements"
          value={stats.openRequirements}
          icon="📋"
          accent="orange"
          onClick={() => openDrillDownModal({
            title: 'Open Requirements',
            rows: drillDown.openRequirements,
            mode: 'requirement',
          })}
        />
        <DashboardCard
          title="Source Profiles"
          value={stats.candidates}
          icon="🧑‍💼"
          accent="teal"
          onClick={() => openDrillDownModal({
            title: 'Source Profiles',
            rows: drillDown.sourceProfiles,
          })}
        />
        <DashboardCard
          title="Present Interviews"
          value={stats.interviews}
          icon="🎯"
          accent="blue"
          onClick={() => openDrillDownModal({
            title: 'Present Interviews',
            rows: drillDown.interviews,
            showInterviewDate: true,
          })}
        />
        <DashboardCard
          title={ADMIN_DASHBOARD_HR_FOLLOW_UP_CARD.title}
          value={stats.hrFollowUp ?? 0}
          icon={ADMIN_DASHBOARD_HR_FOLLOW_UP_CARD.icon}
          accent={ADMIN_DASHBOARD_HR_FOLLOW_UP_CARD.accent}
          onClick={() => openDrillDownModal({
            title: ADMIN_DASHBOARD_HR_FOLLOW_UP_CARD.title,
            rows: drillDown.hrFollowUp || [],
            mode: 'hrFollowUp',
          })}
        />
      </div>

      <div className="hr-dashboard-panels">
        <section className="hr-card">
          <h3 className="hr-section-title">Quick Actions</h3>
          <div className="hr-dashboard-grid">
            {quickLinks.map((item) =>
              item.action ? (
                <button
                  key={item.title}
                  type="button"
                  className="hr-dashboard-card hr-dashboard-card-btn"
                  onClick={() => openFormModal(item.action)}
                >
                  <h3>{item.title}</h3>
                  <p>{item.description}</p>
                </button>
              ) : (
                <Link key={item.to} to={item.to} className="hr-dashboard-card">
                  <h3>{item.title}</h3>
                  <p>{item.description}</p>
                </Link>
              )
            )}
          </div>
        </section>

        <section className="hr-card">
          <h3 className="hr-section-title">Latest Requirements</h3>
          {recentRequirements.length === 0 ? (
            <p className="hr-empty-state">No High priority requirements (P1–P5) found.</p>
          ) : (
            <div className="hr-recent-list">
              {recentRequirements.map((req) => (
                req.id ? (
                  <Link
                    key={req.id}
                    to={`/Requirments?requirementId=${encodeURIComponent(req.id)}`}
                    className="hr-recent-item hr-recent-item-link"
                  >
                    <div>
                      <strong>{req.role}</strong>
                      <span>{req.client}</span>
                    </div>
                    <span className="hr-badge info">{req.type}</span>
                  </Link>
                ) : (
                  <div key={`${req.role}-${req.client}`} className="hr-recent-item">
                    <div>
                      <strong>{req.role}</strong>
                      <span>{req.client}</span>
                    </div>
                    <span className="hr-badge info">{req.type}</span>
                  </div>
                )
              ))}
            </div>
          )}
        </section>
      </div>

      <AppModal
        show={drillDownModal.show}
        onHide={closeDrillDownModal}
        title={drillDownModal.title}
        subtitle={`${drillDownModal.rows.length} record${drillDownModal.rows.length === 1 ? '' : 's'}`}
        size="xl"
        footer={(
          <Button variant="secondary" onClick={closeDrillDownModal}>
            Close
          </Button>
        )}
      >
        <DataTable
          columns={drillDownModalColumns}
          data={drillDownModal.rows}
          rowKey={drillDownRowKey}
          hideToolbar
          searchPlaceholder={`Search ${drillDownModal.title.toLowerCase()}`}
          searchKeys={drillDownSearchKeys}
          emptyMessage={
            drillDownModal.mode === 'requirement'
              ? 'No active open requirements found.'
              : `No ${drillDownModal.title.toLowerCase()} records for the selected date range.`
          }
          pageSize={10}
          showRowNumbers
        />
      </AppModal>
    </PageShell>
    </DashboardSourceProfileSearch>
  );
}

export default AdminDashboard;
